<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { resolveSavedIconClass } from 'dashboard/helper/legacyIconClass';

import Button from 'dashboard/components-next/button/Button.vue';
import ListAttribute from 'dashboard/components-next/CustomAttributes/ListAttribute.vue';
import CheckboxAttribute from 'dashboard/components-next/CustomAttributes/CheckboxAttribute.vue';
import DateAttribute from 'dashboard/components-next/CustomAttributes/DateAttribute.vue';
import OtherAttribute from 'dashboard/components-next/CustomAttributes/OtherAttribute.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();
const route = useRoute();

const pending = ref(false);
const errorMessage = ref('');
const failedOperation = ref(null);
const context = `${route.params.accountId}-${route.params.contactId}`;
const runOperation = async operation => {
  if (pending.value) return;
  pending.value = true;
  errorMessage.value = '';
  failedOperation.value = operation;
  const id = route.params.contactId;
  try {
    if (operation.type === 'delete')
      await store.dispatch('contacts/deleteCustomAttributes', {
        id,
        customAttributes: [props.attribute.attributeKey],
      });
    else
      await store.dispatch('contacts/update', {
        id,
        customAttributes: { [props.attribute.attributeKey]: operation.value },
      });
    if (context !== `${route.params.accountId}-${route.params.contactId}`)
      return;
    failedOperation.value = null;
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.API.SUCCESS_MESSAGE'));
  } catch {
    if (context === `${route.params.accountId}-${route.params.contactId}`)
      errorMessage.value = t('WORKSPACE_V2.CONTACT_DETAIL.SAVE_ERROR');
  } finally {
    pending.value = false;
  }
};
const handleDelete = () => runOperation({ type: 'delete' });
const handleUpdate = value => runOperation({ type: 'update', value });

const componentMap = {
  list: ListAttribute,
  checkbox: CheckboxAttribute,
  date: DateAttribute,
  default: OtherAttribute,
};

const CurrentAttributeComponent = computed(() => {
  return (
    componentMap[props.attribute.attributeDisplayType] || componentMap.default
  );
});
</script>

<template>
  <fieldset
    :disabled="pending"
    class="grid grid-cols-[140px,1fr] group/attribute items-center w-full gap-2"
    :class="isEditingView ? 'min-h-10' : 'min-h-11'"
  >
    <div class="flex items-center gap-1.5 truncate">
      <!--
        SiteUP V2 (F1.2e): o ícone salvo em custom_attribute_definitions.icon
        pode ser legado (`i-lucide-*`). Traduz na leitura — nada foi migrado.
      -->
      <span
        v-if="attribute.icon"
        :class="resolveSavedIconClass(attribute.icon)"
        class="flex-shrink-0 size-4 text-n-slate-11"
      />
      <span class="text-sm font-medium truncate text-n-slate-12">
        {{ attribute.attributeDisplayName }}
      </span>
    </div>

    <component
      :is="CurrentAttributeComponent"
      :attribute="attribute"
      :is-editing-view="isEditingView"
      @update="handleUpdate"
      @delete="handleDelete"
    />
    <div
      v-if="errorMessage"
      role="alert"
      class="col-span-2 text-sm text-n-ruby-11"
    >
      <p>{{ errorMessage }}</p>
      <Button
        :label="t('WORKSPACE_V2.CONTACT_DETAIL.RETRY')"
        @click="runOperation(failedOperation)"
      />
    </div>
  </fieldset>
</template>
